import { addImageParams, getItemUrl } from '@/utils/urls';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { CompleteAccountModalContext, ModalContext } from '@/redux/modules/modal';
import { EventContext } from '@/redux/modules/analytics';
import { getItemFirstImageUrl, getItemModelTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { Helmet } from 'react-helmet-async';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useCallback, useMemo } from 'react';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useNavigate } from 'react-router-dom';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useTrackMainItemClickedPriceResults } from '@/hooks/kameleoon/tracking/useTrackPriceResultsPage';
import ImageLoader from '@/components/ImageLoader/ImageLoader';

const IMAGE_MAX_DIMENSION = 282;
const IMAGE_QUALITY_LEVEL = 95;

type Props = {
    className?: string;
    itemId: number;
};

export const PriceResultsItemImage = ({ className, itemId }: Props) => {
    const url = useAppSelector((state) => getItemFirstImageUrl(state, itemId));
    const navigate = useNavigate();
    const title = useAppSelector((state) => getItemModelTitle(state, itemId));
    const itemPageUrl = getItemUrl(itemId, title);
    const { formatMessage } = useIntl();
    const verifyLogin = useRequireLogin();

    const imageUrl = addImageParams({
        height: IMAGE_MAX_DIMENSION,
        quality: IMAGE_QUALITY_LEVEL,
        url,
        width: IMAGE_MAX_DIMENSION,
    });
    const imageUrlHighDpi = addImageParams({
        height: IMAGE_MAX_DIMENSION,
        quality: IMAGE_QUALITY_LEVEL,
        url,
        width: IMAGE_MAX_DIMENSION * 2,
    });

    const modalContext = useMemo<ModalContext>(
        () => ({
            eventContext: EventContext.seeSoldPrice,
            id: itemId,
            itemId,
            type: CompleteAccountModalContext.SeeSoldPrice,
        }),
        [itemId]
    );

    const trackMainItemCardClicked = useTrackMainItemClickedPriceResults();

    const handleUnlock = useCallback(async () => {
        await verifyLogin({
            modalContext,
            requireLogin: true,
            runFunction: () => {
                trackMainItemCardClicked();
                navigate({ pathname: itemPageUrl });
            },
        });
    }, [itemPageUrl, modalContext, navigate, trackMainItemCardClicked, verifyLogin]);

    return (
        <div className={className}>
            <Helmet>
                <link
                    as="image"
                    href={imageUrl}
                    rel="preload"
                />
            </Helmet>
            <Link
                aria-label={formatMessage({ id: 'itemPage.soldItem.image.alt' }, { title })}
                className="hui-focus-visible-outline block"
                href={itemPageUrl}
                onClick={(e) => {
                    e.preventDefault();
                    handleUnlock();
                }}
            >
                <ImageLoader
                    alt={formatMessage({ id: 'itemPage.soldItem.image.alt' }, { title })}
                    className={cn(
                        'flex h-[282px] w-[282px] cursor-pointer items-center justify-center border border-solid border-border-secondary object-contain',
                        // MOBILE
                        'xsMax:h-[248px] xsMax:w-[248px]'
                    )}
                    height={IMAGE_MAX_DIMENSION}
                    loading="eager"
                    src={imageUrl}
                    srcSet={`${imageUrlHighDpi} 2x`}
                    width={IMAGE_MAX_DIMENSION}
                />
            </Link>
        </div>
    );
};
